/**
 * usePledgeMutations — Mutations for pledge creation, acceptance, and cancellation.
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RewardPledge, RewardPledgeStatus } from '@/types/rewards';
import { usePledgePointsIntegration } from './usePledgePointsIntegration';
import { useGoalRewardIntegration } from '@/hooks/rewards/useGoalRewardIntegration';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

export interface PledgeMutationsContext {
  toast: ReturnType<typeof useToast>;
  fetchPledges: () => Promise<void>;
}

export function usePledgeMutations(context: PledgeMutationsContext) {
  const { toast, fetchPledges } = context;
  const { linkRewardToTodo, markProofSubmitted, markProofReviewed } = useGoalRewardIntegration();
  const {
    awardPledgeCreatedPoints,
    awardPledgeAcceptedPoints,
    awardProofSubmittedPoints,
    awardProofApprovedRecipientPoints,
    awardProofApprovedGiverPoints,
  } = usePledgePointsIntegration();
  
  const createPledge = async (pledgeData: {
    toUserId?: string;
    phoneNumber?: string;
    message?: string;
    rewardId?: string;
    goalId?: string;
    todoId?: string;
    customRewardDetails?: string;
    pointBounty?: number;
    deliveryChannel?: 'in_app' | 'whatsapp';
    recipientPhone?: string;
    recipientLabel?: string;
    attachmentUrl?: string;
  }) => {
    try {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      
      const { data, error } = await supabase.rpc(
        'create_reward_pledge',
        {
          from_user_id_param: userId,
          to_user_id_param: pledgeData.toUserId || null,
          phone_number_param: pledgeData.phoneNumber || null,
          reward_id_param: pledgeData.rewardId || null,
          goal_id_param: pledgeData.goalId || null,
          todo_id_param: pledgeData.todoId || null,
          custom_reward_details_param: pledgeData.customRewardDetails || null,
          message_param: pledgeData.message || null
        }
      );
      
      if (error) {
        logger.error('RPC Error:', error.message);
        throw error;
      }
      
      if (data && (pledgeData.todoId || pledgeData.goalId)) {
        await linkRewardToTodo(data.id, pledgeData.todoId, pledgeData.goalId);
      }

      // Persist optional point bounty (RPC doesn't accept it directly)
      if (data?.id && pledgeData.pointBounty && pledgeData.pointBounty > 0) {
        await supabase
          .from('reward_pledges')
          .update({ point_bounty: pledgeData.pointBounty })
          .eq('id', data.id);
        (data as any).point_bounty = pledgeData.pointBounty;
      }

      // Persist Assign-flow extras (delivery channel, recipient label/phone, attachment).
      if (data?.id && (pledgeData.deliveryChannel || pledgeData.recipientPhone || pledgeData.recipientLabel || pledgeData.attachmentUrl)) {
        const updates: Record<string, unknown> = {};
        if (pledgeData.deliveryChannel) updates.delivery_channel = pledgeData.deliveryChannel;
        if (pledgeData.recipientPhone) updates.recipient_phone = pledgeData.recipientPhone;
        if (pledgeData.recipientLabel) updates.recipient_label = pledgeData.recipientLabel;
        if (pledgeData.attachmentUrl) updates.attachment_url = pledgeData.attachmentUrl;
        await supabase.from('reward_pledges').update(updates as any).eq('id', data.id);
      }

      // Refetch the row so the caller gets public_token + extras.
      let fullRow: any = data;
      if (data?.id) {
        const { data: fresh } = await supabase
          .from('reward_pledges')
          .select('*')
          .eq('id', data.id)
          .maybeSingle();
        if (fresh) fullRow = fresh;
      }

      if (fullRow?.from_user_id && userId) {
        await awardPledgeCreatedPoints(fullRow.id, userId);
        // Queue first-gift-sent milestone (idempotent)
        try {
          await supabase.rpc("queue_pending_reward", {
            p_action: "first_gift_sent" as any,
            p_points: 5,
            p_reference_id: fullRow.id,
            p_description: "You pledged your first reward",
          });
        } catch { /* fire-and-forget */ }
      }
      
      const isPhoneGift = pledgeData.phoneNumber && !pledgeData.toUserId;
      const isWhatsApp = pledgeData.deliveryChannel === 'whatsapp';
      toast.toast({
        title: "Task sent",
        description: isWhatsApp
          ? "Opening WhatsApp… if it doesn't open, use the Copy link option. +3 points!"
          : isPhoneGift
            ? "A gift link has been sent to the phone number. +3 points!"
            : "Reward pledge sent successfully. +3 points!",
      });
      
      await fetchPledges();
      return fullRow as RewardPledge;
    } catch (error: any) {
      logger.error('Error creating reward pledge:', error);
      toast.toast({ title: "Error", description: error.message || "Failed to send reward pledge", variant: "destructive" });
      return null;
    }
  };

  const updatePledgeStatus = useCallback(async (pledgeId: string, status: RewardPledgeStatus, feedback?: string) => {
    try {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      
      const { data, error } = await supabase.rpc(
        'update_pledge_status',
        {
          pledge_id_param: pledgeId,
          status_param: status as any,
          feedback_param: feedback || null,
          user_id_param: userId
        }
      );
      
      if (error) throw error;
      
      toast.toast({ title: "Success", description: `Pledge ${status} successfully` });
      await fetchPledges();
      return data as RewardPledge;
    } catch (error: any) {
      logger.error('Error updating pledge status:', error);
      toast.toast({ title: "Error", description: error.message || `Failed to update pledge status to ${status}`, variant: "destructive" });
      return null;
    }
  }, [toast, fetchPledges]);
  
  const submitProof = async (pledgeId: string, proofData: { submissionText?: string, imageUrls: string[], linkUrls: string[] }) => {
    try {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      
      const { data, error } = await supabase.rpc(
        'submit_proof',
        {
          pledge_id_param: pledgeId,
          user_id_param: userId,
          submission_text_param: proofData.submissionText || null,
          image_urls_param: proofData.imageUrls,
          link_urls_param: proofData.linkUrls
        }
      );
      
      if (error) throw error;
      
      if (data?.pledge_id) {
        const { data: todos } = await supabase.from('todos').select('id').eq('reward_pledge_id', data.pledge_id);
        if (todos && todos.length > 0) {
          await markProofSubmitted(todos[0].id);
        }
      }

      if (userId) {
        await awardProofSubmittedPoints(pledgeId, userId);
      }
      
      toast.toast({ title: "Success", description: "Proof submitted successfully! +10 points" });
      await fetchPledges();
      
      const typedData = data as unknown as any;
      return typedData;
    } catch (error: any) {
      logger.error('Error submitting proof:', error);
      toast.toast({ title: "Error", description: error.message || "Failed to submit proof", variant: "destructive" });
      return null;
    }
  };
  
  const reviewProof = async (proofId: string, isApproved: boolean, feedback?: string) => {
    try {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      
      const { data, error } = await supabase.rpc(
        'review_proof',
        { proof_id_param: proofId, user_id_param: userId, approved_param: isApproved, feedback_param: feedback || null }
      );
      
      if (error) throw error;
      
      if (data?.pledge_id) {
        const { data: todos } = await supabase.from('todos').select('id').eq('reward_pledge_id', data.pledge_id);
        if (todos && todos.length > 0) {
          await markProofReviewed(todos[0].id);
        }
      }

      if (isApproved && data) {
        const { data: pledge } = await supabase.from('reward_pledges').select('to_user_id, from_user_id').eq('id', data.pledge_id).single();
        if (pledge) {
          if (pledge.to_user_id) await awardProofApprovedRecipientPoints(data.pledge_id, pledge.to_user_id);
          if (pledge.from_user_id) await awardProofApprovedGiverPoints(data.pledge_id, pledge.from_user_id);
        }
      }
      
      toast.toast({
        title: "Success",
        description: isApproved ? "Proof approved! +25 points to recipient, +5 to you!" : "Proof rejected with feedback.",
      });
      
      await fetchPledges();
      return data as any;
    } catch (error: any) {
      logger.error('Error reviewing proof:', error);
      toast.toast({ title: "Error", description: error.message || "Failed to review proof", variant: "destructive" });
      return null;
    }
  };

  const cancelPledge = useCallback(async (pledgeId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('cancel_reward_pledge', { pledge_id_param: pledgeId });
      if (error) throw error;
      toast.toast({ title: "Success", description: "Reward pledge cancelled successfully" });
      await fetchPledges();
      return true;
    } catch (error: any) {
      logger.error("Error cancelling pledge:", error);
      toast.toast({ title: "Error", description: error.message || "Failed to cancel pledge", variant: "destructive" });
      return false;
    }
  }, [toast, fetchPledges]);

  const cleanupDuplicates = useCallback(async (): Promise<number> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.rpc('cleanup_duplicate_pledges', { user_id_param: user.id });
      if (error) throw error;
      const deletedCount = data || 0;
      if (deletedCount > 0) {
        toast.toast({ title: "Success", description: `Removed ${deletedCount} duplicate pledge(s)` });
        await fetchPledges();
      } else {
        toast.toast({ title: "Info", description: "No duplicate pledges found" });
      }
      return deletedCount;
    } catch (error: any) {
      logger.error("Error cleaning up duplicates:", error);
      toast.toast({ title: "Error", description: error.message || "Failed to clean up duplicates", variant: "destructive" });
      return 0;
    }
  }, [toast, fetchPledges]);

  const acceptPledge = async (pledgeId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('accept_reward_pledge', { pledge_id_param: pledgeId });
      if (error) throw error;
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) await awardPledgeAcceptedPoints(pledgeId, user.id);
      toast.toast({ title: "Success", description: "Reward accepted! +5 points. You can now work on completing it." });
      await fetchPledges();
      return true;
    } catch (error: any) {
      logger.error("Error accepting pledge:", error);
      toast.toast({ title: "Error", description: error.message || "Failed to accept reward", variant: "destructive" });
      return false;
    }
  };

  const fulfillPledge = useCallback(async (pledgeId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('reward_pledges')
        .update({ status: 'fulfilled', updated_at: new Date().toISOString() })
        .eq('id', pledgeId);
      if (error) throw error;
      toast.toast({ title: "Gift Fulfilled! 🎉", description: "The gift promise has been marked as fulfilled." });
      await fetchPledges();
      return true;
    } catch (error: any) {
      logger.error("Error fulfilling pledge:", error);
      toast.toast({ title: "Error", description: error.message || "Failed to fulfill pledge", variant: "destructive" });
      return false;
    }
  }, [toast, fetchPledges]);

  // ===== Honor-system Circle flow =====
  const markPledgeDone = useCallback(async (pledgeId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('mark_pledge_done', { pledge_id_param: pledgeId });
      if (error) throw error;
      toast.toast({ title: 'Marked done', description: 'Waiting for the assigner to approve.' });
      await fetchPledges();
      return true;
    } catch (e: any) {
      logger.error('mark_pledge_done failed', e);
      toast.toast({ title: 'Error', description: e.message || 'Failed to mark done', variant: 'destructive' });
      return false;
    }
  }, [toast, fetchPledges]);

  const approvePledge = useCallback(async (pledgeId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('approve_pledge', { pledge_id_param: pledgeId });
      if (error) throw error;
      toast.toast({ title: 'Approved! ⭐', description: 'Bounty transferred and a gold star awarded.' });
      // Funnel analytics — fire-and-forget
      import('@/utils/analyticsEvents').then(m =>
        m.trackAssignEvent('assign_approved', { pledge_id: pledgeId })
      ).catch(() => { /* ignore */ });
      await fetchPledges();
      return true;
    } catch (e: any) {
      logger.error('approve_pledge failed', e);
      toast.toast({ title: 'Error', description: e.message || 'Failed to approve', variant: 'destructive' });
      return false;
    }
  }, [toast, fetchPledges]);

  const rejectPledge = useCallback(async (pledgeId: string, reason: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('reject_pledge', { pledge_id_param: pledgeId, reason_param: reason });
      if (error) throw error;
      toast.toast({ title: 'Sent back', description: 'The recipient can mark it done again.' });
      import('@/utils/analyticsEvents').then(m =>
        m.trackAssignEvent('assign_rejected', { pledge_id: pledgeId, reason })
      ).catch(() => { /* ignore */ });
      await fetchPledges();
      return true;
    } catch (e: any) {
      logger.error('reject_pledge failed', e);
      toast.toast({ title: 'Error', description: e.message || 'Failed to reject', variant: 'destructive' });
      return false;
    }
  }, [toast, fetchPledges]);

  return { createPledge, updatePledgeStatus, submitProof, reviewProof, cancelPledge, cleanupDuplicates, acceptPledge, fulfillPledge, markPledgeDone, approvePledge, rejectPledge };
}
